'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import type {
  ResourceFilters,
  ResourceItem,
  ActiveFilters,
  PaginationMeta,
  FiltersApiResponse,
  ResourcesApiResponse,
  SortOrder,
  UseResourceHubOptions,
  UseResourceHubReturn,
} from '../types/ResourceHub'

const SEARCH_OR_FIELDS = [
  'title',
  'description',
  // 'resourceCategory', //TODO: queto è un problema perchè nel cms ho gli slug
]

const FILTER_PARAM_MAP: Record<string, string> = {
  category: 'resourceCategory',
  industry: 'industry',
  gas: 'gas',
}

const REVERSE_FILTER_PARAM_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(FILTER_PARAM_MAP).map(([param, key]) => [key, param])
)

type ParsedUrlParams = {
  activeFilters: ActiveFilters
  searchQuery: string
  sortOrder: SortOrder
  page: number
}

function parseUrlParams(): ParsedUrlParams {
  if (typeof window === 'undefined') {
    return { activeFilters: {}, searchQuery: '', sortOrder: 'asc', page: 1 }
  }

  const params = new URLSearchParams(window.location.search)
  const activeFilters: ActiveFilters = {}

  Object.entries(FILTER_PARAM_MAP).forEach(([paramName, filterKey]) => {
    const value = params.get(paramName)
    if (value) {
      activeFilters[filterKey] = value.split(',').filter(Boolean)
    }
  })

  const searchQuery = params.get('q') || ''
  const sort = params.get('sort')
  const sortOrder: SortOrder = sort === 'desc' ? 'desc' : 'asc'
  const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1)

  return { activeFilters, searchQuery, sortOrder, page }
}

function syncUrlParams(state: { activeFilters: ActiveFilters; searchQuery: string; sortOrder: SortOrder; page: number }): void {
  if (typeof window === 'undefined') return

  const params = new URLSearchParams()

  if (state.searchQuery) params.set('q', state.searchQuery)

  Object.entries(state.activeFilters).forEach(([filterKey, values]) => {
    if (values && values.length > 0) {
      const paramName = REVERSE_FILTER_PARAM_MAP[filterKey] || filterKey
      params.set(paramName, values.join(','))
    }
  })

  if (state.sortOrder !== 'asc') params.set('sort', state.sortOrder)
  if (state.page > 1) params.set('page', String(state.page))

  const search = params.toString()
  const url = search ? `${window.location.pathname}?${search}` : window.location.pathname
  window.history.replaceState({}, '', url)
}

export default function useResourceHub({
  pageSize = 12,
  locale = 'it',
  initialPage = 1,
  enabled = true,
  searchQuery = '',
  sortOrder: initialSortOrder = 'asc',
  syncSearchParams = false,
}: UseResourceHubOptions = {}): UseResourceHubReturn {

  const urlParamsRef = useRef<ParsedUrlParams | null>(
    syncSearchParams ? parseUrlParams() : null
  )
  const urlParams = urlParamsRef.current

  const hasUrlParams = urlParams !== null && (
    !!urlParams.searchQuery || Object.keys(urlParams.activeFilters).length > 0
  )

  const [currentPage, setCurrentPage] = useState(urlParams?.page ?? initialPage)
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(urlParams?.activeFilters ?? {})
  const [sortOrder, setSortOrder] = useState<SortOrder>(urlParams?.sortOrder ?? initialSortOrder)
  
  // API Configuration
  const getApiBaseUrl = (): string => {
    return process.env.NEXT_PUBLIC_API_URL || ''
  }

  const getSiteName = (): string => {
    return process.env.NEXT_PUBLIC_SITE_NAME || ''
  }

  // Endpoints (hardcoded)
  const ENDPOINTS = {
    resourcesFilters: (lang: string) => `api/resources/filters?locale=${lang}`,
    resources: (site: string, lang: string) => `api/resources/${lang}/${site}`,
  }

  // API Functions
  const fetchResourceFilters = async (lang: string): Promise<ResourceFilters | undefined> => {
    const baseUrl = getApiBaseUrl()
    const endpoint = ENDPOINTS.resourcesFilters(lang)
    const url = `${baseUrl}/${endpoint}`

    console.log('fetchResourceFilters url', url)

    const response = await fetch(url, { cache: 'no-store' })
    if (!response.ok) {
      throw new Error(`Failed to fetch resource filters: ${response.statusText}`)
    }

    const data: FiltersApiResponse = await response.json()
    return data?.data
  }

  const fetchResources = async (
    lang: string,
    params: { page?: number; pageSize?: number; filters?: ActiveFilters; search?: string; sort?: SortOrder } = {}
  ): Promise<ResourcesApiResponse> => {
    const baseUrl = getApiBaseUrl()
    const site = getSiteName()

    if (!site) {
      return { data: [], meta: { pagination: { page: 1, pageSize: 12, pageCount: 0, total: 0 } } }
    }

    const { page = 1, pageSize = 12, filters = {}, search = '', sort = 'asc' } = params
    const endpoint = ENDPOINTS.resources(site, lang)

    const queryParams = new URLSearchParams({
      'pagination[page]': String(page),
      'pagination[pageSize]': String(pageSize),
      'sort[0]': `title:${sort}`,
    })

    Object.entries(filters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        values.forEach((value, index) => {
          queryParams.append(`filters[${key}][$in][${index}]`, value)
        })
      }
    })

    const trimmedSearch = search.trim()
    if (trimmedSearch) {
      SEARCH_OR_FIELDS.forEach((field, index) => {
        queryParams.append(`filters[$or][${index}][${field}][$containsi]`, trimmedSearch)
      })
    }

    console.log('fetchResources url', `${baseUrl}/${endpoint}?${queryParams.toString()}`)

    const url = `${baseUrl}/${endpoint}?${queryParams.toString()}`

    const response = await fetch(url, { cache: 'no-store' })
    if (!response.ok) {
      throw new Error(`Failed to fetch resources: ${response.statusText}`)
    }

    const data: ResourcesApiResponse = await response.json()
    return data
  }

  // Fetch resource filters (static, fetched once)
  const filtersQuery = useQuery({
    queryKey: ['resourceFilters', locale],
    queryFn: () => fetchResourceFilters(locale),
    staleTime: Infinity,
    enabled,
  })

  // Fetch resources with pagination and filters
  const resourcesQuery = useQuery({
    queryKey: ['resources', locale, currentPage, pageSize, activeFilters, searchQuery, sortOrder],
    queryFn: () =>
      fetchResources(locale, {
        page: currentPage,
        pageSize,
        filters: activeFilters,
        search: searchQuery,
        sort: sortOrder,
      }),
    placeholderData: keepPreviousData,
    enabled,
    retry: false,
  })

  const pagination: PaginationMeta = resourcesQuery.data?.meta?.pagination ?? {
    page: currentPage,
    pageSize,
    pageCount: 0,
    total: 0,
  }

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1) return
      if (pagination.pageCount > 0 && page > pagination.pageCount) return
      setCurrentPage(page)
    },
    [pagination.pageCount]
  )

  const nextPage = useCallback(() => {
    if (currentPage < pagination.pageCount) {
      setCurrentPage((prev) => prev + 1)
    }
  }, [currentPage, pagination.pageCount])

  const prevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1)
    }
  }, [currentPage])

  const updateFilter = useCallback((key: string, value: string) => {
    setActiveFilters((prev) => {
      const current = prev[key] ?? []
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]

      if (next.length === 0) {
        const { [key]: _removed, ...rest } = prev
        void _removed
        return rest
      }
      return { ...prev, [key]: next }
    })
    setCurrentPage(1)
  }, [])

  const clearFilter = useCallback((key: string) => {
    setActiveFilters((prev) => {
      const { [key]: _removed, ...rest } = prev
      void _removed
      return rest
    })
    setCurrentPage(1)
  }, [])

  const clearFilters = useCallback(() => {
    setActiveFilters({})
    setCurrentPage(1)
  }, [])

  const refetch = useCallback(() => {
    resourcesQuery.refetch()
  }, [resourcesQuery])

  useEffect(() => {
    if (!syncSearchParams) return
    syncUrlParams({ activeFilters, searchQuery, sortOrder, page: currentPage })
  }, [syncSearchParams, activeFilters, searchQuery, sortOrder, currentPage])

  return {
    // Resources data
    items: resourcesQuery.data?.data ?? [],
    isLoading: resourcesQuery.isLoading,
    isFetching: resourcesQuery.isFetching,
    isError: resourcesQuery.isError,
    error: resourcesQuery.error,

    // Pagination
    currentPage: pagination.page,
    totalPages: pagination.pageCount,
    totalItems: pagination.total,
    hasNextPage: currentPage < pagination.pageCount,
    hasPrevPage: currentPage > 1,

    // Pagination actions
    goToPage,
    nextPage,
    prevPage,

    // Filters
    filters: filtersQuery.data,
    isFiltersLoading: filtersQuery.isLoading,
    activeFilters,
    setActiveFilters: (filters: ActiveFilters) => {
      setActiveFilters(filters)
      setCurrentPage(1)
    },
    updateFilter,
    clearFilter,
    clearFilters,

    // Sorting
    sortOrder,
    setSortOrder,

    // Refetch
    refetch,

    // URL params
    initialSearchQuery: urlParams?.searchQuery ?? '',
    hasUrlParams,
  }
}
